import { useCallback } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { Box, Card } from '@mui/material';

import { Typography, Divider } from '@occc-app/design-system';
import { FormLayout } from '@occc-app/routes/CampaignComponents/components/FormLayout';
import { ScheduleDates } from '@occc-app/routes/CampaignComponents/components/ScheduleDates';
import TriggerOnFulfillment from '@occc-app/routes/CampaignComponents/components/TriggerOnFulfillment/TriggerOnFulfillment';
import TriggerOnOfferVisibilityChange from '@occc-app/routes/CampaignComponents/components/TriggerOnOfferVisibilityChange/TriggerOnOfferVisibilityChange';
import { TriggerOnProgress } from '@occc-app/routes/CampaignComponents/components/TriggerOnProgress/TriggerOnProgress';
import { CampaignComponentFooterActions } from '@occc-app/routes/CampaignComponents/components/CampaignComponentFooterActions';
import { CampaignComponentReadOnlyFooterActions } from '@occc-app/routes/CampaignComponents/components/CampaignComponentReadOnlyFooterActions';
import { CanEditFormFields } from '@occc-app/App/CanEditFormFieldsContext';
import { Flow_Types } from '@occc-app/offerHelpers/types';
import { ScheduleFormProps, ScheduleFormValues } from './types';
import { OfferForm } from '../components/OfferForm';

export const ScheduleForm = ({
  initialValues,
  loading,
  currentFlowType,
  validate,
  onSubmit,
  onCancelClick,
  isFormEditable,
  isTriggerOnVisibilityShown,
  isTriggerOnProgressShown,
  isTriggerOnFulfillmentShown,
}: ScheduleFormProps) => {
  const renderForm: FormRenderProps<ScheduleFormValues>['render'] = useCallback(
    ({ handleSubmit, submitting, values, dirty }: FormRenderProps<any>) => {
      const handleCancelClick = () => {
        onCancelClick(dirty);
      };

      const formFooterActionButtons = isFormEditable ? (
        <CampaignComponentFooterActions
          currentFlowType={currentFlowType}
          onCancel={handleCancelClick}
          isSubmitDisabled={submitting || (currentFlowType === Flow_Types.Update && !dirty)}
        />
      ) : (
        <CampaignComponentReadOnlyFooterActions
          onCancel={handleCancelClick}
          onNextStepClick={handleCancelClick}
        />
      );

      return (
        <OfferForm handleSubmit={handleSubmit} dirty={!submitting && dirty}>
          <FormLayout actions={formFooterActionButtons} type='primary' isLoading={loading}>
            <Card>
              <Box p={3}>
                <Typography variant='h6' paragraph>
                  Schedule Offer
                </Typography>
                <ScheduleDates values={values} />
              </Box>

              {isTriggerOnVisibilityShown && (
                <>
                  <Divider />
                  <TriggerOnOfferVisibilityChange
                    open={values?.triggerPreferences?.triggerOnVisibility}
                  />
                </>
              )}

              {isTriggerOnFulfillmentShown && (
                <>
                  <Divider />
                  <TriggerOnFulfillment open={values?.triggerPreferences?.triggerOnFulfillment} />
                </>
              )}

              {isTriggerOnProgressShown && (
                <>
                  <Divider />
                  <TriggerOnProgress open={values?.triggerPreferences?.triggerOnProgress} />
                </>
              )}
            </Card>
          </FormLayout>
        </OfferForm>
      );
    },
    [
      isFormEditable,
      currentFlowType,
      isTriggerOnVisibilityShown,
      isTriggerOnFulfillmentShown,
      isTriggerOnProgressShown,
      onCancelClick,
      loading,
    ],
  );

  return (
    <CanEditFormFields isEditable={isFormEditable}>
      <Form<ScheduleFormValues>
        initialValues={initialValues}
        onSubmit={onSubmit}
        render={renderForm}
        validate={validate}
      />
    </CanEditFormFields>
  );
};
